import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "./axiosInstance";
import { toast } from "react-toastify";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const syncSession = useCallback(async (config = {}) => {
    try {
      const response = await axiosInstance.get("/me", {
        ...config,
        suppressToast: config.suppressToast ?? true,
      });

      const sessionUser = response.data?.data;
      if (!sessionUser?.id) {
        throw new Error("Invalid session response");
      }

      setUser(sessionUser);
      return sessionUser;
    } catch (error) {
      setUser(null);
      throw error;
    }
  }, []);

  const clearSession = useCallback(({ redirectToLogin = false, notify = false } = {}) => {
    setUser(null);
    localStorage.removeItem("ritika_session_active");

    if (notify) {
      toast.info("Logged out");
    }

    if (redirectToLogin) {
      navigate("/login");
    }
  }, [navigate]);

  const logout = useCallback(async ({ redirectToLogin = true, notify = true } = {}) => {
    try {
      await axiosInstance.post("/auth/logout", null, {
        skipAuthRefresh: true,
        suppressToast: true,
      });
    } catch {}

    clearSession({ redirectToLogin, notify });
  }, [clearSession]);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const hasSession = localStorage.getItem("ritika_session_active") === "true";
        if (!hasSession) {
          setLoading(false);
          return;
        }
        const sessionUser = await syncSession();
        if (isMounted) {
          setUser(sessionUser);
        }
      } catch (err) {
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, [syncSession]);

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.message === 'AUTHENTICATION_FAILED' || error.response?.status === 401) {
          await logout({ redirectToLogin: true, notify: false });
        }
        return Promise.reject(error);
      }
    );

    return () => axiosInstance.interceptors.response.eject(interceptor);
  }, [logout]);

  const login = async (credentials) => {
    try {
      await axiosInstance.post("/auth/login", credentials, {
        skipAuthRefresh: true,
        suppressToast: true,
      });

      const sessionUser = await syncSession({ skipAuthRefresh: true });
      localStorage.setItem("ritika_session_active", "true");
      toast.success("Login successful");
      navigate("/");
      return sessionUser;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Login failed");
      throw err;
    }
  };

  const signup = async (data) => {
    await axiosInstance.post("/auth/signup", data, {
      skipAuthRefresh: true,
      suppressToast: true,
    });
    toast.success("Account created");
    navigate("/login");
    return true;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshSession: syncSession }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
